import {
  removeCleanOrNotDefined,
  showCmsFormValidationErrorToast,
  useCatalogueRaisonneeSpecificArtworksFormData,
  useCatalogueRaisonneGeneralFormData,
  useCatalogueRaisonneSpecificMutliplesFormData,
  useCreateCatalogueRaisonneGeneral,
  useCreateCatalogueRaisonneSpecifique,
  useGeneralSettingsFormData,
  useSections,
  useUpdateCatalogueRaisonneGeneral,
  useUpdateCatalogueRaisonneSections,
  useUpdateCatalogueRaisonneSpecifique,
  useUpdateGeneralSettings,
  wrapHandleSubmitWithAction,
} from "@/api/cms";
import {
  CatalogueRaisonneGeneralForm,
  CatalogueRaisonneSpecificForm,
} from "@/components/cms/CatalogueRaisonneForm";
import DecoupageCatalogueRaisonneForm from "@/components/cms/DecoupageCatalogueRaisonneForm";
import * as AF from "@/components/form-items/AbstractForm";
import { CmsPageAppearanceConfig } from "@/components/cms/theme/CmsPageAppearanceConfig";
import { DoubleMultilingualField } from "@/components/form-items/MultilingualField";
import { useAuth } from "@/context/auth/AuthContext";
import { useCmsWebsiteLanguages } from "@/hooks/use-cms-website-languages";
import { useToast } from "@/hooks/use-toast";
import {
  constructCmsLocaleDict,
  multilingualFieldSchema,
  SupportedLocale,
} from "@/i18n";
import CMSLayout from "@/layout/CMSLayout.tsx";
import {
  CatalogueRaisonneSection,
  catalogueRaisonneGeneralContent,
  catalogueRaisonneSectionUpsertSchema,
  catalogueRaisonneSpecificContent,
  cmsPageLocaleBaseSchemaShape,
  withPageNameRequiredWhenPublished,
} from "@/lib/cms/cms-types";

/** Tableau vide stable — évite `?? []` qui crée une nouvelle référence à chaque render. */
const EMPTY_SECTIONS: CatalogueRaisonneSection[] = [];
import { consumeDrawerAppearanceSaveErrorToastShown } from "@/lib/cms/cms-page-appearance-drawer-save";
import {
  buildCmsPagePatchPayload,
  isCmsLocaleSectionDirty,
  pageThemeFromDto,
} from "@/lib/cms/page-theme";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { FormProvider, Path, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";

const catalogueRaisonneGeneralSchema = multilingualFieldSchema(
  withPageNameRequiredWhenPublished(
    cmsPageLocaleBaseSchemaShape.merge(
      catalogueRaisonneGeneralContent.omit({
        id: true,
      }),
    ),
  ),
);

const catalogueRaisonneSpecificSchema = multilingualFieldSchema(
  withPageNameRequiredWhenPublished(
    cmsPageLocaleBaseSchemaShape.merge(
      catalogueRaisonneSpecificContent.omit({
        id: true,
      }),
    ),
  ),
);

const formSchema = z.object({
  general: catalogueRaisonneGeneralSchema,
  specificArtworks: catalogueRaisonneSpecificSchema,
  specificMultiples: catalogueRaisonneSpecificSchema,
});

type FormData = z.infer<typeof formSchema>;

const sectionSchema = z.object({
  type: z.string().optional(),
  target: z.enum(["ARTWORKS", "MULTIPRINTS"]).optional(),
  decoupages: z.array(catalogueRaisonneSectionUpsertSchema).optional(),
  sortByInsideDecoupage: z.string().optional(),
});

type SectionData = z.infer<typeof sectionSchema>;

const CRCms = () => {
  const { t } = useTranslation();
  const { websiteLanguages } = useCmsWebsiteLanguages();
  const { instanceData } = useAuth();
  const cmsID = instanceData?.CMS.id;

  const { toast } = useToast();

  const { data: formGeneralData } = useCatalogueRaisonneGeneralFormData();
  const updateGeneral = useUpdateCatalogueRaisonneGeneral();
  const createGeneral = useCreateCatalogueRaisonneGeneral();
  const { data: generalSettingsData } = useGeneralSettingsFormData();
  const updateGeneralSettings = useUpdateGeneralSettings();

  const {
    data: formSpecificArtworksData,
    isLoading: isLoadingSpecificArtworks,
    error: errorSpecificArtworks,
  } = useCatalogueRaisonneeSpecificArtworksFormData();
  const {
    data: formSpecificMultiplesData,
    isLoading: isLoadingSpecificMultiples,
    error: errorSpecificMultiples,
  } = useCatalogueRaisonneSpecificMutliplesFormData();

  const {
    data: decoupagesArtworks,
    isLoading: isLoadingDecoupagesArtworks,
    error: errorDecoupagesArtworks,
  } = useSections(cmsID ?? "", "ARTWORKS");
  const {
    data: decoupagesMultiples,
    isLoading: isLoadingDecoupagesMultiples,
    error: errorDecoupagesMultiples,
  } = useSections(cmsID ?? "", "MULTIPRINTS");

  const updateSpecific = useUpdateCatalogueRaisonneSpecifique();
  const createSpecific = useCreateCatalogueRaisonneSpecifique();

  const formGeneralDefaultValues = useMemo(
    () =>
      constructCmsLocaleDict((lang) => {
        const general = formGeneralData?.[lang];
        return {
          ...general?.content,
          published: general?.published ?? false,
          pageName: general?.pageName ?? "",
          ...pageThemeFromDto(general),
        };
      }, websiteLanguages),
    [formGeneralData, websiteLanguages],
  );

  const formSpecificArtworksDefaultValues: FormData["specificArtworks"] =
    useMemo(
      () =>
        constructCmsLocaleDict((lang) => {
          const specificArtworks = formSpecificArtworksData?.[lang];
          return {
            type: "ARTWORKS" as const,
            content: specificArtworks?.content?.content ?? "",
            runningTitle: specificArtworks?.content?.runningTitle ?? "",
            showRegistrationNumber:
              specificArtworks?.content?.showRegistrationNumber ?? false,
            publishedTitle:
              specificArtworks?.content?.publishedTitle ??
              specificArtworks?.content?.publishedText ??
              true,
            publishedText: specificArtworks?.content?.publishedText ?? true,
            publishedImage: specificArtworks?.content?.publishedImage ?? true,
            published: specificArtworks?.published ?? false,
            pageName: specificArtworks?.pageName ?? "",
            ...pageThemeFromDto(specificArtworks),
          };
        }, websiteLanguages),
      [formSpecificArtworksData, websiteLanguages],
    );

  const formSpecificMultiplesDefaultValues: FormData["specificMultiples"] =
    useMemo(
      () =>
        constructCmsLocaleDict((lang) => {
          const specificMultiples = formSpecificMultiplesData?.[lang];
          return {
            type: "MULTIPRINTS" as const,
            content: specificMultiples?.content?.content ?? "",
            runningTitle: specificMultiples?.content?.runningTitle ?? "",
            showRegistrationNumber:
              specificMultiples?.content?.showRegistrationNumber ?? false,
            publishedTitle:
              specificMultiples?.content?.publishedTitle ??
              specificMultiples?.content?.publishedText ??
              true,
            publishedText: specificMultiples?.content?.publishedText ?? true,
            publishedImage: specificMultiples?.content?.publishedImage ?? true,
            published: specificMultiples?.published ?? false,
            pageName: specificMultiples?.pageName ?? "",
            ...pageThemeFromDto(specificMultiples),
          };
        }, websiteLanguages),
      [formSpecificMultiplesData, websiteLanguages],
    );

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      general: formGeneralDefaultValues,
      specificArtworks: formSpecificArtworksDefaultValues,
      specificMultiples: formSpecificMultiplesDefaultValues,
    },
  });

  const crModeForm = useForm<{ isMixedCRMode: boolean }>({
    defaultValues: {
      isMixedCRMode: generalSettingsData?.isMixedCRMode ?? false,
    },
  });

  // Only reset when we first have data; do not reset on refetch (e.g. tab focus)
  const hasInitGeneral = useRef(false);
  const hasInitSpecificArtworks = useRef(false);
  const hasInitSpecificMultiples = useRef(false);
  const hasInitCrMode = useRef(false);
  const hasInitDecoupageArtworks = useRef(false);
  const hasInitDecoupageMultiples = useRef(false);

  useEffect(() => {
    if (
      formGeneralData != null &&
      !hasInitGeneral.current &&
      Object.keys(formGeneralDefaultValues).length > 0
    ) {
      form.reset({
        ...form.getValues(),
        general: formGeneralDefaultValues,
      });
      hasInitGeneral.current = true;
    }
  }, [formGeneralDefaultValues, form, formGeneralData]);

  useEffect(() => {
    if (
      formSpecificArtworksData != null &&
      !hasInitSpecificArtworks.current &&
      Object.keys(formSpecificArtworksDefaultValues).length > 0
    ) {
      form.reset({
        ...form.getValues(),
        specificArtworks: formSpecificArtworksDefaultValues,
      });
      hasInitSpecificArtworks.current = true;
    }
  }, [formSpecificArtworksDefaultValues, form, formSpecificArtworksData]);

  useEffect(() => {
    if (
      formSpecificMultiplesData != null &&
      !hasInitSpecificMultiples.current &&
      Object.keys(formSpecificMultiplesDefaultValues).length > 0
    ) {
      form.reset({
        ...form.getValues(),
        specificMultiples: formSpecificMultiplesDefaultValues,
      });
      hasInitSpecificMultiples.current = true;
    }
  }, [formSpecificMultiplesDefaultValues, form, formSpecificMultiplesData]);

  const formDecoupageArtworks = useForm<SectionData>({
    resolver: zodResolver(sectionSchema),
    defaultValues: {
      type: undefined,
      target: "ARTWORKS",
      decoupages: [],
      sortByInsideDecoupage: "dateCreation:asc",
    },
  });

  const formDecoupageMultipleprints = useForm<SectionData>({
    resolver: zodResolver(sectionSchema),
    defaultValues: {
      type: undefined,
      target: "MULTIPRINTS",
      decoupages: [],
      sortByInsideDecoupage: "dateCreation:asc",
    },
  });

  /**
   * Restore "type" field values cleared by the clean-up step setting all empty fields to undefined.
   * Since "type" is not user-controlled, it doesn't reappear when the user inputs something in the form,
   * which makes for a validation error on the next submit if not restored.
   */
  const restoreMissingTypeFields = () => {
    websiteLanguages.forEach((lang) => {
      const artworksType = form.getValues(
        `specificArtworks.${lang}.type` as Path<FormData>,
      );
      if (artworksType === undefined) {
        form.setValue(
          `specificArtworks.${lang}.type` as Path<FormData>,
          "ARTWORKS",
        );
      }

      const multiplesType = form.getValues(
        `specificMultiples.${lang}.type` as Path<FormData>,
      );
      if (multiplesType === undefined) {
        form.setValue(
          `specificMultiples.${lang}.type` as Path<FormData>,
          "MULTIPRINTS",
        );
      }
    });
  };

  useEffect(() => {
    if (decoupagesArtworks && !hasInitDecoupageArtworks.current) {
      formDecoupageArtworks.reset({
        type: decoupagesArtworks[0]?.type ?? "creationDate",
        target: "ARTWORKS",
        decoupages: decoupagesArtworks,
        sortByInsideDecoupage:
          decoupagesArtworks[0]?.sortByInsideDecoupage ?? "dateCreation:asc",
      });
      hasInitDecoupageArtworks.current = true;
    }
  }, [decoupagesArtworks, formDecoupageArtworks]);

  useEffect(() => {
    if (decoupagesMultiples && !hasInitDecoupageMultiples.current) {
      formDecoupageMultipleprints.reset({
        type: decoupagesMultiples[0]?.type ?? "creationDate",
        target: "MULTIPRINTS",
        decoupages: decoupagesMultiples,
        sortByInsideDecoupage:
          decoupagesMultiples[0]?.sortByInsideDecoupage ?? "dateCreation:asc",
      });
      hasInitDecoupageMultiples.current = true;
    }
  }, [decoupagesMultiples, formDecoupageMultipleprints]);

  const { mutate: mutateSections } = useUpdateCatalogueRaisonneSections();

  const submitDecoupagesArtworks = async (data: SectionData) => {
    if (
      isLoadingSpecificArtworks ||
      errorSpecificArtworks ||
      isLoadingSpecificMultiples ||
      errorSpecificMultiples ||
      !data.decoupages
    )
      return;

    if (cmsID == null) {
      console.error("Tried to submit but cmsId is null");
      return;
    }

    const sortBy = data.sortByInsideDecoupage ?? "dateCreation:asc";
    mutateSections(
      {
        cmsId: cmsID,
        type: data.type ?? "creationDate",
        target: data.target ?? "ARTWORKS",
        decoupages: data.decoupages.map((d) => ({
          ...d,
          sortByInsideDecoupage: sortBy,
        })),
      },
      {
        onSuccess: () => {
          toast({
            title: t("success"),
            description: t("decoupageSuccessMessage"),
            duration: 4000,
            variant: "success",
          });
        },
        onError: (error: any) => {
          console.error("Error updating catalogue raisonné:", error);
          toast({
            title: t("error"),
            description: t("decoupageErrorMessage"),
            variant: "error",
            duration: 4000,
          });
        },
      },
    );
  };

  const submitArtworksDecoupages = async () => {
    if (isLoadingDecoupagesArtworks || errorDecoupagesArtworks) return;
    const data = formDecoupageArtworks.getValues();
    const sortBy = formDecoupageArtworks.getValues("sortByInsideDecoupage");
    submitDecoupagesArtworks({
      ...data,
      target: "ARTWORKS",
      sortByInsideDecoupage:
        sortBy ?? data.sortByInsideDecoupage ?? "dateCreation:asc",
    });
  };

  const submitMultipleprintsDecoupages = async () => {
    if (isLoadingDecoupagesMultiples || errorDecoupagesMultiples) return;

    const data = formDecoupageMultipleprints.getValues();
    const sortBy = formDecoupageMultipleprints.getValues(
      "sortByInsideDecoupage",
    );
    submitDecoupagesArtworks({
      ...data,
      target: "MULTIPRINTS",
      sortByInsideDecoupage:
        sortBy ?? data.sortByInsideDecoupage ?? "dateCreation:asc",
    });
  };

  const onSubmit = async (submittedData: FormData) => {
    submitArtworksDecoupages();
    submitMultipleprintsDecoupages();

    const pageSaves: Promise<unknown>[] = [];

    Object.keys(submittedData.general).forEach((key) => {
      const localeData = submittedData.general[key as SupportedLocale];
      const baseData = formGeneralData?.[key as SupportedLocale];
      if (!localeData || !isCmsLocaleSectionDirty(form, `general.${key}`)) return;

      const mutation = baseData?.content.id ? updateGeneral : createGeneral;

      const {
        pageName,
        published,
        pageBackgroundColorId,
        pageTextColorMode,
        pageTextColorId,
        ...content
      } = localeData;
      const pageNameStr = pageName ?? "";

      pageSaves.push(
        mutation.mutateAsync({
          id: baseData?.content.id ?? "",
          lang: key as SupportedLocale,
          data: buildCmsPagePatchPayload({
            pageName: pageNameStr,
            published,
            pageBackgroundColorId,
            pageTextColorMode,
            pageTextColorId,
            content,
          }),
        }),
      );
    });

    Object.keys(submittedData.specificArtworks).forEach((key) => {
      const localeData = submittedData.specificArtworks[key as SupportedLocale];
      const baseData = formSpecificArtworksData?.[key as SupportedLocale];
      if (!localeData || !isCmsLocaleSectionDirty(form, `specificArtworks.${key}`))
        return;

      const mutation = baseData?.content.id ? updateSpecific : createSpecific;

      const {
        pageName,
        published,
        pageBackgroundColorId,
        pageTextColorMode,
        pageTextColorId,
        pageFontFamily,
        pageFontVariant,
        content: htmlContent,
        runningTitle,
        type,
        showRegistrationNumber,
        publishedTitle,
        publishedText,
        publishedImage,
      } = localeData;
      const pageNameStr = pageName ?? "";

      pageSaves.push(
        mutation.mutateAsync({
          id: baseData?.content.id ?? "",
          lang: key as SupportedLocale,
          data: buildCmsPagePatchPayload({
            pageName: pageNameStr,
            published,
            pageBackgroundColorId,
            pageTextColorMode,
            pageTextColorId,
            pageFontFamily,
            pageFontVariant,
            content: {
              content: htmlContent,
              runningTitle,
              type: type ?? "ARTWORKS",
              showRegistrationNumber: showRegistrationNumber ?? false,
              publishedTitle: publishedTitle ?? true,
              publishedText: publishedText ?? true,
              publishedImage: publishedImage ?? true,
            },
          }),
        }),
      );
    });

    Object.keys(submittedData.specificMultiples).forEach((key) => {
      const localeData =
        submittedData.specificMultiples[key as SupportedLocale];
      const baseData = formSpecificMultiplesData?.[key as SupportedLocale];
      if (
        !localeData ||
        !isCmsLocaleSectionDirty(form, `specificMultiples.${key}`)
      )
        return;

      const mutation = baseData?.content.id ? updateSpecific : createSpecific;

      const {
        pageName,
        published,
        pageBackgroundColorId,
        pageTextColorMode,
        pageTextColorId,
        pageFontFamily,
        pageFontVariant,
        content: htmlContent,
        runningTitle,
        type,
        showRegistrationNumber,
        publishedTitle,
        publishedText,
        publishedImage,
      } = localeData;
      const pageNameStr = pageName ?? "";

      pageSaves.push(
        mutation.mutateAsync({
          id: baseData?.content.id ?? "",
          lang: key as SupportedLocale,
          data: buildCmsPagePatchPayload({
            pageName: pageNameStr,
            published,
            pageBackgroundColorId,
            pageTextColorMode,
            pageTextColorId,
            pageFontFamily,
            pageFontVariant,
            content: {
              content: htmlContent,
              runningTitle,
              type: type ?? "MULTIPRINTS",
              showRegistrationNumber: showRegistrationNumber ?? false,
              publishedTitle: publishedTitle ?? true,
              publishedText: publishedText ?? true,
              publishedImage: publishedImage ?? true,
            },
          }),
        }),
      );
    });

    restoreMissingTypeFields();

    if (pageSaves.length === 0) return;

    try {
      await Promise.all(pageSaves);
      toast({
        title: t("success"),
        description: t("homepageSuccessMessage"),
        duration: 4000,
        variant: "success",
      });
    } catch (error) {
      console.error("Error saving catalogue raisonné page:", error);
      if (!consumeDrawerAppearanceSaveErrorToastShown()) {
        toast({
          title: t("error"),
          description: t("decoupageErrorMessage"),
          variant: "error",
          duration: 4000,
        });
      }
    }
  };

  useEffect(() => {
    if (!hasInitCrMode.current && generalSettingsData != null) {
      crModeForm.reset({
        isMixedCRMode: generalSettingsData?.isMixedCRMode ?? false,
      });
      hasInitCrMode.current = true;
    }
  }, [crModeForm, generalSettingsData?.isMixedCRMode]);

  // Watch the form value in real-time for immediate UI updates
  const crModeFormValue = crModeForm.watch("isMixedCRMode");

  const onSubmitCRMode = useCallback(
    (data: { isMixedCRMode: boolean }) => {
      if (instanceData?.CMS?.id == null || !generalSettingsData) {
        console.error(
          "Tried to update but there is no CMS or generalSettingsData",
        );
        return;
      }

      updateGeneralSettings.mutate(
        {
          id: instanceData.CMS.id,
          ...generalSettingsData,
          isMixedCRMode: data.isMixedCRMode,
        },
        {
          onSuccess: () => {
            toast({
              title: t("success"),
              description: t("decoupageSuccessMessage"),
              duration: 4000,
              variant: "success",
            });
          },
          onError: (error: any) => {
            console.error("Error updating isMixedCRMode:", error);
            toast({
              title: t("error"),
              description: t("decoupageErrorMessage"),
              variant: "error",
              duration: 4000,
            });
          },
        },
      );
    },
    [
      instanceData?.CMS?.id,
      generalSettingsData,
      updateGeneralSettings,
      toast,
      t,
    ],
  );

  // Auto-submit when value changes
  const previousValueRef = useRef(generalSettingsData?.isMixedCRMode ?? false);

  useEffect(() => {
    // Update ref when server data changes
    previousValueRef.current = generalSettingsData?.isMixedCRMode ?? false;
  }, [generalSettingsData?.isMixedCRMode]);

  useEffect(() => {
    // Only submit if the value has actually changed from the server value
    if (
      crModeFormValue !== undefined &&
      crModeFormValue !== previousValueRef.current &&
      generalSettingsData &&
      instanceData?.CMS?.id
    ) {
      const timer = setTimeout(() => {
        crModeForm.handleSubmit(onSubmitCRMode)();
        previousValueRef.current = crModeFormValue;
      }, 300); // Debounce to avoid too many requests
      return () => clearTimeout(timer);
    }
  }, [
    crModeFormValue,
    crModeForm,
    generalSettingsData,
    instanceData?.CMS?.id,
    onSubmitCRMode,
  ]);

  const cleanBeforeSubmit = () => {
    removeCleanOrNotDefined(form, formGeneralData, "general");
    removeCleanOrNotDefined(form, formSpecificArtworksData, "specificArtworks");
    removeCleanOrNotDefined(
      form,
      formSpecificMultiplesData,
      "specificMultiples",
    );
  };

  // Use form value if available, otherwise fallback to server data
  const isMixedCRMode =
    crModeFormValue ?? generalSettingsData?.isMixedCRMode ?? false;

  const tabs = useMemo(() => {
    const baseTabs = [
      {
        value: "general",
        label: t("cms.catalogueRaisonne.tabs.general"),
      },
    ];

    if (isMixedCRMode) {
      // Mixed mode: only show artworks tab with mixed label, and artworks structure
      return [
        ...baseTabs,
        {
          value: "specific-artworks",
          label: t("cms.catalogueRaisonne.tabs.artworksMixed"),
        },
        {
          value: "structure-artworks",
          label: t("cms.catalogueRaisonne.tabs.structure.artworksMixed"),
        },
      ];
    } else {
      // Separate mode: show all tabs
      return [
        ...baseTabs,
        {
          value: "specific-artworks",
          label: t("cms.catalogueRaisonne.tabs.artworks"),
        },
        {
          value: "specific-multiples",
          label: t("cms.catalogueRaisonne.tabs.multiples"),
        },
        {
          value: "structure-artworks",
          label: t("cms.catalogueRaisonne.tabs.structure.artworks"),
        },
        {
          value: "structure-multiples",
          label: t("cms.catalogueRaisonne.tabs.structure.multiples"),
        },
      ];
    }
  }, [isMixedCRMode, t]);

  const [searchParams] = useSearchParams();
  const appearanceFormPrefix = useMemo(() => {
    const tab = searchParams.get("tab") ?? "general";
    switch (tab) {
      case "general":
        return "general";
      case "specific-artworks":
        return "specificArtworks";
      case "specific-multiples":
        return "specificMultiples";
      default:
        return undefined;
    }
  }, [searchParams]);

  return (
    <CMSLayout
      pageName={t("cmsTabCatalogueRaisonne")}
      formIds={["catalogue-raisonne"]}
      className="flex flex-col gap-4"
    >
      <form
        id="catalogue-raisonne"
        style={{ width: "100%" }}
        onSubmit={wrapHandleSubmitWithAction(
          form,
          cleanBeforeSubmit,
          onSubmit,
          (errors) => {
            console.error("Validation errors:", errors);
            showCmsFormValidationErrorToast();
          },
        )}
      >
        <AF.AbstractForm
          form={form}
          formKey="cms-catalogue-raisonne"
          tabs={tabs}
          model="cms"
          classname="pt-0 px-0"
        >
          {appearanceFormPrefix && (
            <CmsPageAppearanceConfig
              formId="catalogue-raisonne"
              formPrefix={appearanceFormPrefix}
            />
          )}
          <AF.Tab value="general">
            <AF.Col>
              <AF.Row>
                <DoubleMultilingualField
                  corner={(lang) => (
                    <AF.Switch
                      dispLabel={false}
                      name={`general.${lang}.published`}
                    />
                  )}
                  render={(lang) => (
                    <CatalogueRaisonneGeneralForm
                      formBase={`general.${lang}`}
                      isMixedCRMode={crModeFormValue}
                    />
                  )}
                />
              </AF.Row>
              <FormProvider {...crModeForm}>
                <form
                  onSubmit={crModeForm.handleSubmit(onSubmitCRMode)}
                  className="w-full"
                >
                  <div className="flex flex-col rounded-2xl bg-gray-50 border border-gray-300 p-4 items-center justify-between gap-2 w-full">
                    <p className="text-lg font-medium text-gray-600">
                      {t("cms.catalogueRaisonne.generalSettings.title")}
                    </p>
                    <AF.Switch
                      name="isMixedCRMode"
                      label={t(
                        "cms.catalogueRaisonne.general.form.isMixedCRMode",
                      )}
                    />
                  </div>
                </form>
              </FormProvider>
            </AF.Col>
          </AF.Tab>
          <AF.Tab value="specific-artworks">
            <div className="flex w-full flex-col gap-6">
            <DoubleMultilingualField
              corner={(lang) => (
                <AF.Switch
                  dispLabel={false}
                  name={`specificArtworks.${lang}.published`}
                />
              )}
              render={(lang) => (
                <CatalogueRaisonneSpecificForm
                  positionBase="CRAccueil"
                  formBase={`specificArtworks.${lang}`}
                  isMixedCRMode={crModeFormValue}
                />
              )}
            />
            </div>
          </AF.Tab>
          {!isMixedCRMode && (
            <AF.Tab value="specific-multiples">
            <div className="flex w-full flex-col gap-6">
              <DoubleMultilingualField
                corner={(lang) => (
                  <AF.Switch
                    dispLabel={false}
                    name={`specificMultiples.${lang}.published`}
                  />
                )}
                render={(lang) => (
                  <CatalogueRaisonneSpecificForm
                    positionBase="CRAccueilEM"
                    formBase={`specificMultiples.${lang}`}
                    isMixedCRMode={crModeFormValue}
                  />
                )}
              />
            </div>
            </AF.Tab>
          )}
          <AF.Tab value="structure-artworks" isForm={false} showHeader={false}>
            <FormProvider {...formDecoupageArtworks}>
              <DecoupageCatalogueRaisonneForm
                form={formDecoupageArtworks}
                target="ARTWORKS"
                sections={decoupagesArtworks ?? EMPTY_SECTIONS}
                cmsID={cmsID ?? ""}
              />
            </FormProvider>
          </AF.Tab>
          {!isMixedCRMode && (
            <AF.Tab
              value="structure-multiples"
              isForm={false}
              showHeader={false}
            >
              <FormProvider {...formDecoupageMultipleprints}>
                <DecoupageCatalogueRaisonneForm
                  form={formDecoupageMultipleprints}
                  target="MULTIPRINTS"
                  sections={decoupagesMultiples ?? EMPTY_SECTIONS}
                  cmsID={cmsID ?? ""}
                />
              </FormProvider>
            </AF.Tab>
          )}
        </AF.AbstractForm>
      </form>
    </CMSLayout>
  );
};

export default CRCms;
